import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { stockAPI } from "../../lib/api";
import { ArrowLeft, PackagePlus, PackageMinus, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const MOVEMENT_TYPES = [
    { value: "entrada", label: "Entrada (compra de tiragem)", icon: PackagePlus,  color: "text-green-400", border: "border-green-700", bg: "bg-green-900/20" },
    { value: "saida",   label: "Saída (venda manual)",        icon: PackageMinus, color: "text-red-400",   border: "border-red-700",   bg: "bg-red-900/20" },
    { value: "ajuste",  label: "Ajuste (doação / brinde)",    icon: ArrowUpDown,  color: "text-blue-400",  border: "border-blue-700",  bg: "bg-blue-900/20" },
];

export default function StockEntry() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [form, setForm] = useState({ product_id: "", type: "entrada", quantity: 1, reason: "", buyer_name: "", buyer_address: "", notes: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        stockAPI.products().then(r => {
            const p = r.data || [];
            setProducts(p);
            if (p.length > 0) setForm(f => ({...f, product_id: p[0].id}));
        }).catch(() => {});
    }, []);

    const selectedProduct = products.find(p => p.id === form.product_id);

    const save = async () => {
        if (!form.product_id || !form.quantity) { toast.error("Produto e quantidade são obrigatórios"); return; }
        if (form.type === "saida" && selectedProduct && form.quantity > selectedProduct.stock_current) {
            toast.error(`Estoque insuficiente. Disponível: ${selectedProduct.stock_current}`);
            return;
        }
        setSaving(true);
        try {
            if (form.type === "entrada" || form.type === "ajuste") {
                await stockAPI.entry({ ...form, quantity: Number(form.quantity) });
            } else {
                await stockAPI.exit({ ...form, quantity: Number(form.quantity) });
            }
            toast.success("Movimentação registrada");
            navigate("/estoque");
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar"); }
        finally { setSaving(false); }
    };

    return (
        <div className="p-6 max-w-xl space-y-6">
            <div className="flex items-center gap-3">
                <button onClick={() => navigate("/estoque")}
                    className="p-2 rounded-lg text-stone-400 hover:text-stone-100 hover:bg-stone-800 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-stone-100">Movimentação de Estoque</h1>
                    <p className="text-stone-400 text-sm">Registrar entrada, saída ou ajuste</p>
                </div>
            </div>

            {/* Tipo */}
            <div className="space-y-2">
                <label className="text-xs text-stone-400 font-medium">Tipo de movimentação</label>
                <div className="grid grid-cols-3 gap-3">
                    {MOVEMENT_TYPES.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.value} onClick={() => setForm(f => ({...f, type: t.value}))}
                                className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${
                                    form.type === t.value
                                        ? `${t.bg} ${t.border}`
                                        : "border-stone-700 hover:border-stone-600"
                                }`}>
                                <Icon className={`w-5 h-5 ${form.type === t.value ? t.color : "text-stone-500"}`} />
                                <span className={`text-xs text-center leading-tight ${form.type === t.value ? "text-stone-200" : "text-stone-500"}`}>
                                    {t.value.charAt(0).toUpperCase() + t.value.slice(1)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4">
                {/* Produto */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1">Produto *</label>
                    <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                        value={form.product_id} onChange={e => setForm(f => ({...f, product_id: e.target.value}))}>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} (estoque: {p.stock_current})</option>)}
                    </select>
                    {selectedProduct && (
                        <p className="text-xs text-stone-500 mt-1">
                            Estoque atual: <strong className={selectedProduct.stock_current <= selectedProduct.stock_min ? "text-red-400" : "text-stone-300"}>
                                {selectedProduct.stock_current}
                            </strong> · Mínimo: {selectedProduct.stock_min}
                        </p>
                    )}
                </div>

                {/* Quantidade */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1">Quantidade *</label>
                    <input type="number" min={1}
                        className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                        value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))} />
                </div>

                {/* Motivo */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1">Motivo</label>
                    <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                        value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                        placeholder={form.type === "entrada" ? "ex: Compra de 500 livros — tiragem 2ª ed." : form.type === "saida" ? "ex: Venda direta" : "ex: Doação para evento"} />
                </div>

                {/* Campos de saída (comprador) */}
                {form.type === "saida" && (
                    <>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Nome do comprador</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.buyer_name} onChange={e => setForm(f => ({...f, buyer_name: e.target.value}))} />
                        </div>
                        <div>
                            <label className="text-xs text-stone-400 block mb-1">Endereço de entrega</label>
                            <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                                value={form.buyer_address} onChange={e => setForm(f => ({...f, buyer_address: e.target.value}))} />
                        </div>
                    </>
                )}

                {/* Notas */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1">Notas internas</label>
                    <textarea rows={2} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                        value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
                </div>
            </div>

            <div className="flex gap-3">
                <button onClick={() => navigate("/estoque")}
                    className="flex-1 py-2.5 border border-stone-700 rounded-lg text-sm text-stone-400 hover:text-stone-200 transition-colors">
                    Cancelar
                </button>
                <button onClick={save} disabled={saving}
                    className="flex-1 py-2.5 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg font-medium disabled:opacity-50 transition-colors">
                    {saving ? "Salvando..." : "Registrar Movimentação"}
                </button>
            </div>
        </div>
    );
}
